import { useMemo } from "react";

export interface FieldStep<TFormValues> {
    name: keyof TFormValues;
    deps: Array<keyof TFormValues>;
    extraActiveCheck?: (values: Partial<TFormValues>) => boolean;
}

type DeepPartial<T> = {
    [P in keyof T]?: T[P] extends Array<infer U>
        ? Array<DeepPartial<U>>
        : T[P] extends object
          ? DeepPartial<T[P]>
          : T[P];
};

export type FieldFlowMap<TFormValues> = Record<
    keyof TFormValues,
    FieldStep<TFormValues>
>;

// ============== Клас для роботи з залежностями ==============

class FormDependencyManager<TFormValues> {
    private fieldFlowMap: FieldFlowMap<TFormValues>;

    private dependenciesCache = new Map<
        keyof TFormValues,
        Set<keyof TFormValues>
    >();

    private dependentsCache = new Map<
        keyof TFormValues,
        Array<keyof TFormValues>
    >();

    constructor(fieldFlowMap: FieldFlowMap<TFormValues>) {
        this.fieldFlowMap = fieldFlowMap;
    }

    // Рекурсивно знаходить всі залежності поля
    private getAllDependencies(
        fieldName: keyof TFormValues,
        visited = new Set<keyof TFormValues>(),
    ): Set<keyof TFormValues> {
        if (visited.has(fieldName)) return visited;

        visited.add(fieldName);

        const field = this.fieldFlowMap[fieldName];

        if (!field) return visited;

        field.deps.forEach((dep) => {
            this.getAllDependencies(dep, visited);
        });

        return visited;
    }

    // Отримати всі залежності поля (з кешуванням)
    getDependencies(fieldName: keyof TFormValues): Set<keyof TFormValues> {
        if (!this.dependenciesCache.has(fieldName)) {
            this.dependenciesCache.set(
                fieldName,
                this.getAllDependencies(fieldName),
            );
        }

        return this.dependenciesCache.get(fieldName)!;
    }

    // Знайти всі поля, що залежать від даного поля
    getDependents(fieldName: keyof TFormValues): Array<keyof TFormValues> {
        if (!this.dependentsCache.has(fieldName)) {
            const dependents: Array<keyof TFormValues> = [];

            (
                Object.values(this.fieldFlowMap) as FieldStep<TFormValues>[]
            ).forEach((field) => {
                const deps = this.getDependencies(field.name);

                if (deps.has(fieldName) && field.name !== fieldName) {
                    dependents.push(field.name);
                }
            });

            this.dependentsCache.set(fieldName, dependents);
        }

        return this.dependentsCache.get(fieldName)!;
    }

    // Перевірити, чи поле активне (всі залежності виконані)
    isFieldActive(
        fieldName: keyof TFormValues,
        values: DeepPartial<TFormValues>,
        customChecks?: {
            [P in keyof TFormValues]?: (
                value: TFormValues[P] | undefined,
            ) => boolean;
        },
    ): boolean {
        const field = this.fieldFlowMap[fieldName];

        if (!field) return false;

        const depsOk = field.deps.every((depName) => {
            const depValue = values[depName];

            if (!this.isFieldActive(depName, values, customChecks)) {
                return false;
            }

            if (customChecks?.[depName]) {
                return customChecks[depName](
                    depValue as TFormValues[typeof depName] | undefined,
                );
            }

            return Boolean(depValue);
        });

        if (!depsOk) return false;

        if (field.extraActiveCheck) {
            return field.extraActiveCheck(values as Partial<TFormValues>);
        }

        return true;
    }

    // Обчислити карту активності для всіх полів
    computeActiveMap(
        values: DeepPartial<TFormValues>,
        customChecks?: {
            [P in keyof TFormValues]?: (
                value: TFormValues[P] | undefined,
            ) => boolean;
        },
    ): Record<keyof TFormValues, boolean> {
        const result = {} as Record<keyof TFormValues, boolean>;

        Object.keys(this.fieldFlowMap).forEach((fieldName) => {
            result[fieldName as keyof TFormValues] = this.isFieldActive(
                fieldName as keyof TFormValues,
                values,
                customChecks,
            );
        });

        return result;
    }

    // Отримати список полів для скидання при зміні певного поля
    getFieldsToReset(fieldName: keyof TFormValues): Array<keyof TFormValues> {
        return this.getDependents(fieldName);
    }

    // Отримати порядок полів (топологічне сортування)
    getFieldOrder(): Array<keyof TFormValues> {
        const visited = new Set<keyof TFormValues>();
        const order: Array<keyof TFormValues> = [];

        const visit = (fieldName: keyof TFormValues) => {
            if (visited.has(fieldName)) return;

            visited.add(fieldName);

            const field = this.fieldFlowMap[fieldName];

            if (field) {
                field.deps.forEach(visit);
            }

            order.push(fieldName);
        };

        Object.keys(this.fieldFlowMap).forEach((fieldName) => {
            visit(fieldName as keyof TFormValues);
        });

        return order;
    }
}

export const useFormDependencies = <TFormValues>(
    fieldFlowMap: FieldFlowMap<TFormValues>,
) => {
    return useMemo(
        () => new FormDependencyManager(fieldFlowMap),
        [fieldFlowMap],
    );
};
